import { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Transition, { TransitionStatus } from 'react-transition-group/Transition';
import tw, { TwStyle } from 'twin.macro';
import 'styled-components/macro';

import { Icon, TextButton } from 'component-lib';

interface LightboxProps {
  src: string;
  alt?: string;
}

const lightboxTransitionStyles: Partial<Record<TransitionStatus, TwStyle>> = {
  entering: tw`opacity-100`,
  entered: tw`opacity-100`,
};

const Backdrop = tw.div`fixed bg-opacity-0 top-0 left-0 w-full h-full bg-indigo flex justify-center items-center transition duration-fast z-overlay`;

export const Lightbox = ({ src, alt = '', ...props }: LightboxProps) => {
  const childContainer = useRef(null);
  const container = useRef(null);
  const lastEventEl = useRef(null);
  const [open, setOpen] = useState<boolean>(false);

  const onClose = () => {
    setOpen(false);
  };

  return (
    <>
      <a
        href={src}
        tw="hover:cursor-zoom-in"
        onClick={e => {
          e.preventDefault();
          setOpen(true);
        }}
      >
        <img src={src} alt={alt} {...props} />
      </a>
      {open
        ? ReactDOM.createPortal(
            <Transition appear in={open} timeout={100} mountOnEnter unmountOnExit>
              {transitionState => (
                <Backdrop
                  ref={container}
                  data-testid="backdrop"
                  onMouseUp={() => {
                    container.current === lastEventEl.current && onClose();
                  }}
                  onMouseDown={() => {
                    lastEventEl.current = container.current;
                  }}
                  css={[transitionState === 'entered' && tw`bg-opacity-70`]}
                >
                  <TextButton tw="top-0 right-0 absolute p-0" onClick={onClose}>
                    <Icon tw="text-xl/loose text-white p-2" name="circle-remove" />
                  </TextButton>
                  <span
                    tw="transition-opacity opacity-0 duration-fast max-h-screen flex flex-col"
                    css={lightboxTransitionStyles[transitionState]}
                    data-testid="content"
                    ref={childContainer}
                    onMouseUp={e => {
                      e.stopPropagation();
                    }}
                    onMouseDown={e => {
                      e.stopPropagation();
                      lastEventEl.current = childContainer.current;
                    }}
                  >
                    <img src={src} alt={alt} {...props} tw="max-h-[90vh] max-w-[95vw] w-auto h-auto" />
                    {alt && <h4 tw="text-white text-sm text-center pt-2">{alt}</h4>}
                  </span>
                </Backdrop>
              )}
            </Transition>,
            document.body,
          )
        : null}
    </>
  );
};
